// src/pages/evcc/EvccGuidanceDocuments.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#ccd87cff";
const sectionHeaderBorder = "#9fad3d";
const linkBlue = "#7e895d";

const docs = [
  {
    section: "Cover Sheet for Model Documents",
    intro:
      "A reference and introductory document that explains how to use each of the EVCC model documents. It also provides design and branding guidance, including instructions for incorporating the EVCC logo.",
    items: [
      {
        title: "Cover Sheet for EVCC Model Documents (pdf)",
        description:
          "Overview of the model documents, how they fit together, and how to apply EVCC branding correctly.",
        href: "/assets/evcc/guidance/cover-sheet-model-documents.pdf",
      },
    ],
  },
  {
    section: "Consumer Contracts Regulations – Scenario Guidance",
    intro:
      "Scenario-based guidance to help you identify which contract you should be using and when, so you can comply with the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013.",
    items: [
      {
        title: "ccr_guidance_v1.pdf",
        description:
          "Complying with the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 – scenario-based guidance for EVCC members.",
        href: "/assets/evcc/guidance/ccr_guidance_v1.pdf",
      },
    ],
  },
  {
    section: "Consumer protection legislation – Data protection",
    intro:
      "Guidance to help you understand your responsibilities when handling consumer data.",
    items: [
      {
        title: "Guidance on General Data Protection (pdf)",
        description:
          "Explains how consumer data may be collected and used, and what you must tell customers at first contact.",
        href: "/assets/evcc/guidance/general-data-protection-guidance.pdf",
      },
    ],
  },
];

function SectionCard({ section }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {section.section}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
        {section.intro && (
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#4b5563", maxWidth: 900 }}
          >
            {section.intro}
          </Typography>
        )}

        <List disablePadding>
          {section.items.map((item, index) => (
            <React.Fragment key={item.title}>
              <ListItemButton
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  alignItems: "flex-start",
                  px: 0,
                  "&:hover .linkText": { textDecoration: "underline" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 48, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: sectionHeaderBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 18, color: "#fff" }} />
                  </Box>
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      className="linkText"
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: linkBlue,
                      }}
                    >
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    item.description && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, color: "#4a4f59", lineHeight: 1.5 }}
                      >
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItemButton>

              {index < section.items.length - 1 && (
                <Divider sx={{ my: 1.75, borderColor: "#edf1f7" }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Paper>
  );
}

export default function EvccGuidanceDocuments() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; EVCC &gt; Guidance
        </Typography>

        {/* Page title + intro */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          EVCC – Guidance
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563", maxWidth: 900 }}>
          Use these guidance documents to select the right EVCC contracts, apply
          the EVCC logo correctly, and comply with key consumer protection and
          data protection requirements.
        </Typography>

        {docs.map((section) => (
          <SectionCard key={section.section} section={section} />
        ))}
      </Container>
    </Box>
  );
}
